  // src/pages/ManageBookings.jsx
  import React, { useEffect, useMemo, useState, useRef } from "react";
  import Header from "../components/Header";
  import Footer from "../components/Footer";
 import {
  fetchOwnerProfile,
  fetchOwnerVehiclesWithBookings,
  fetchOwnerDrivers,
} from "../api/ownerCarsApi";
import {
  fetchOwnerBookingPage,
  fetchOwnerBookingStats,
} from "../api/ownerManageBookingsApi";
  import {
    assignDriverAndAcceptBooking,
    assignDriverOnly,
    cancelBooking,
    fetchOwnerBookingCancelConfig,
    ownerCancelAndReassignBooking,
    selfDriveAssignAndAcceptBooking,
    selfDriveAssignBookingOnly,
  } from "../api/ownerBookingsApi";


  import {
    fetchAvailabilityBlocks,
    createAvailabilityBlock,
    deleteAvailabilityBlock,
  } from "../api/vehicleAvailabilityApi";

  import {
    fetchOwnerMatchRequests,
    acceptOwnerMatchRequest,
    declineOwnerMatchRequest,
  } from "../api/ownerInboxApi";


  import { fetchDriverAvailabilityBlocks } from "../api/driverAvailabilityApi";


  import { useUser } from "../context/UserContext";
  import { toast } from "../lib/toast";
  import { useLocation } from "react-router-dom";

  /* ───────── Constants ───────── */
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  /* ───────── Date helpers + 12h helpers ───────── */

  const toDateSafe = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toDT = (d, t) => {
    if (!d || !t) return new Date("1970-01-01T00:00:00Z");
    return new Date(`${d}T${t}:00`);
  };

  const formatLeadTime = (minutes) => {
    const safe = Number.isFinite(Number(minutes)) ? Math.max(0, Math.floor(Number(minutes))) : 0;
    if (safe === 0) return "pickup time";
    if (safe >= 60 && safe % 60 === 0) {
      const hours = safe / 60;
      return `${hours} hour${hours === 1 ? "" : "s"}`;
    }
    return `${safe} minutes`;
  };

  const fmtDateOnly = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fmtDT = (dateStr, timeStr) =>
    new Date(`${dateStr}T${timeStr}:00`).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const fmtTimeOnly12h = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "—";
    const d = new Date(`${dateStr}T${timeStr}:00`);
    if (Number.isNaN(d.getTime())) return timeStr;
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const STEP_MIN = 15;
  const pad2 = (n) => String(n).padStart(2, "0");

  /* 12h helpers reused by TimePicker12 + labels */
  const to12PartsFrom24 = (hhmm) => {
    if (!hhmm) return { hour12: 9, minute: 0, ampm: "AM" };
    const [hhRaw, mmRaw] = hhmm.split(":");
    const hh = Number(hhRaw);
    const mm = Number(mmRaw);
    const ampm = hh < 12 ? "AM" : "PM";
    const hour12 = hh % 12 === 0 ? 12 : hh % 12;
    return {
      hour12,
      minute: Number.isNaN(mm) ? 0 : mm,
      ampm,
    };
  };

  const partsTo24 = (hour12, minute, ampm) => {
    let h24 = Number(hour12) % 12;
    if (ampm === "PM") h24 += 12;
    if (ampm === "AM" && Number(hour12) === 12) h24 = 0;
    return `${pad2(h24)}:${pad2(minute)}`;
  };

  const to12From24 = (hhmm) => {
    if (!hhmm) return "";
    const { hour12, minute, ampm } = to12PartsFrom24(hhmm);
    return `${hour12}:${pad2(minute)} ${ampm}`;
  };

  /* ───────── Month calendar helpers (for block booking dates) ───────── */

  const toISODate = (d) => {
    if (!d || Number.isNaN(d.getTime?.())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const buildMonthCells = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay(); // 0 = Sunday

    const cells = [];
    const cursor = new Date(year, month, 1 - startWeekday);

    for (let i = 0; i < 42; i += 1) {
      const date = new Date(cursor);
      cells.push({
        date,
        iso: toISODate(date),
        inMonth: date.getMonth() === month,
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return cells;
  };

  const isISOWithin = (iso, startIso, endIso) =>
    iso >= startIso && iso <= endIso;

  /* ───────── Ledger (local ride-state overlay) ───────── */
  const LEDGER_KEY = "ride_state_ledger";
  const QUEUE_KEY = "ride_events_queue";

  const readLedger = () => {
    try {
      return JSON.parse(localStorage.getItem(LEDGER_KEY) || "{}");
    } catch {
      return {};
    }
  };

  const writeLedger = (obj) => {
    try {
      localStorage.setItem(LEDGER_KEY, JSON.stringify(obj || {}));
    } catch {
      // ignore
    }
  };

  const ledgerMerge = (bookingId, patch) => {
    const id = String(bookingId);
    const L = readLedger();
    L[id] = { ...(L[id] || {}), ...(patch || {}) };
    writeLedger(L);
  };

  const ledgerMarkStarted = (id, startedAtISO) =>
    ledgerMerge(id, { startedAt: startedAtISO });

  const ledgerMarkEnded = (id, endedAtISO) =>
    ledgerMerge(id, { endedAt: endedAtISO });

  const flushQueueToLedger = () => {
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
      if (Array.isArray(q)) {
        for (const ev of q) {
          const p = ev?.payload || {};
          if (!p.bookingId) continue;
          if (ev.type?.includes("ride:started") && p.startTime) {
            ledgerMarkStarted(p.bookingId, p.startTime);
          }
          if (ev.type?.includes("ride:completed") && p.endTime) {
            ledgerMarkEnded(p.bookingId, p.endTime);
          }
        }
      }
      localStorage.removeItem(QUEUE_KEY);
    } catch {
      // ignore
    }
  };

  const applyLedgerToList = (rows) => {
    const L = readLedger();
    return (rows || []).map((b) => {
      const led = L[String(b.id)];
      if (!led) return b;
      const out = { ...b };
      if (led.startedAt && !out.startTime && !out?.ride?.startTime) {
        out.startTime = led.startedAt;
      }
      if (led.endedAt) {
        out.endTime = led.endedAt;
        out.status = "COMPLETED";
        out.rideStatus = "COMPLETED";
        out.driverAction = "COMPLETED";
      }
      return out;
    });
  };

  /* ───────── Ride-aware status helpers ───────── */
  const hasStarted = (b) =>
    !!(toDateSafe(b?.ride?.startTime) || toDateSafe(b?.startTime));

  const hasEnded = (b) =>
    !!(toDateSafe(b?.ride?.endTime) || toDateSafe(b?.endTime));

  const isCompletedStrict = (b) =>
    (hasStarted(b) && hasEnded(b)) ||
    ((b.rideStatus || b.status || "").toUpperCase() === "COMPLETED");

  const explicitCancelled = (b) => {
    const s = (b.status || "").toUpperCase();
    const rs = (b.rideStatus || "").toUpperCase();
    return s === "CANCELLED" || rs === "CANCELLED";
  };

  const isMissed = (b, nowDT) => {
    const pickupDT = toDT(b.pickupDate, b.pickupTime);
    return pickupDT < nowDT && !hasStarted(b) && !explicitCancelled(b);
  };

  const isOwnerConfirmed = (b) =>
    (b?.ownerAction || "").toUpperCase() === "CONFIRMED";


  const isPendingOwnerDecision = (b) => {
    if (explicitCancelled(b)) return false;
    if (isCompletedStrict(b)) return false;
    if (hasStarted(b)) return false;

    const ownerConfirmed = isOwnerConfirmed(b);
    const selfDrive = isSelfDriveBooking(b);

    const driverDeclined = (b?.driverAction || "").toUpperCase() === "DECLINED";
    const noDriverAssigned = !b?.assignedDriver;

    // initial owner decision pending
    if (!ownerConfirmed) return true;

    // only needs reassignment when it is NOT self drive
    if (!selfDrive && ownerConfirmed && (driverDeclined || noDriverAssigned))
      return true;

    return false;
  };



  const bothAcceptedAndFuture = (b, nowDT) => {
    const selfDrive = isSelfDriveBooking(b);

    return (
      !isCompletedStrict(b) &&
      !explicitCancelled(b) &&
      !isMissed(b, nowDT) &&
      !hasStarted(b) &&
      isOwnerConfirmed(b) &&
      (selfDrive ||
        (!!b?.assignedDriver &&
          (String(b.driverAction || "").toUpperCase() !== "DECLINED"))) &&
      toDT(b.pickupDate, b.pickupTime) >= nowDT
    );
  };

  /* Time labels */

  const resolvePickupTimeLabel = (b) => {
    const iso = b?.ride?.startTime || b?.startTime;
    if (iso) {
      const d = toDateSafe(iso);
      if (d) {
        return d.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    }
    if (b.pickupDate && b.pickupTime) return fmtDT(b.pickupDate, b.pickupTime);
    return "—";
  };

  const isSelfDriveBooking = (b) => {
    if (!b) return false;

    // explicit booleans (preferred)
    if (b.selfDrive === true || b.isSelfDrive === true) return true;
    if (b.driverRequired === false || b.withDriver === false) return true;

    // string-based fallbacks (safe across backend variations)
    const t = String(
      b.tripType || b.bookingType || b.serviceType || b.rideType || b.planType || ""
    ).toUpperCase();

    if (t === "SELFDRIVE" || t === "SELF_DRIVE") return true;
    if (t.includes("SELF") && t.includes("DRIVE")) return true;

    return false;
  };


  const resolveDropTimeLabel = (b) => {
    const iso =
      b?.ride?.endTime ||
      b?.endTime ||
      b.dropAt ||
      b.estimatedDropAt ||
      b.expectedDropAt ||
      b.actualDropAt;

    if (iso) {
      const d = toDateSafe(iso);
      if (d)
        return d.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
    }
    if (b.dropDate && b.dropTime) return fmtDT(b.dropDate, b.dropTime);
    if (b.dropTime && b.pickupDate) return fmtDT(b.pickupDate, b.dropTime);
    return "—";
  };

  /* ───────── Status meta + chip ───────── */

  const getStatusMeta = (booking, nowDT) => {
    if (explicitCancelled(booking)) return { label: "Cancelled", variant: "cancelled" };
    if (isMissed(booking, nowDT)) return { label: "Missed", variant: "missed" };
    if (isCompletedStrict(booking)) return { label: "Completed", variant: "completed" };
    if (hasStarted(booking) && !hasEnded(booking)) return { label: "In progress", variant: "active" };

    const ownerConfirmed = isOwnerConfirmed(booking);
    const driverDeclined = (booking?.driverAction || "").toUpperCase() === "DECLINED";
    const noDriver = !booking?.assignedDriver;
    // Driver availability blocks (per driver) for the currently-open booking window


    if (ownerConfirmed && (driverDeclined || noDriver) && !isSelfDriveBooking(booking)) {
      return { label: "Needs reassignment", variant: "pending" };
    }
    if (isPendingOwnerDecision(booking)) {
      return { label: "Pending", variant: "pending" };
    }
    if (bothAcceptedAndFuture(booking, nowDT)) {
      return { label: "Upcoming", variant: "upcoming" };
    }
    return { label: "—", variant: "default" };
  };


  const StatusPill = ({ booking, nowDT }) => {
    const { label, variant } = getStatusMeta(booking, nowDT);

    let tone = "bg-slate-100 text-slate-700";
    if (variant === "pending") tone = "bg-amber-100 text-amber-800";
    if (variant === "upcoming") tone = "bg-indigo-100 text-indigo-800";
    if (variant === "active") tone = "bg-sky-100 text-sky-800";
    if (variant === "completed") tone = "bg-emerald-100 text-emerald-800";
    if (variant === "cancelled" || variant === "missed")
      tone = "bg-rose-100 text-rose-800";

    return (
      <span
        className={`inline-flex items-center rounded-[6px] px-2.5 py-1 text-[11px] font-medium ${tone}`}
      >
        {label}
      </span>
    );
  };

  /* ---------------- 12-hour time picker (owner console, shared style) ---------------- */

  function TimePicker12({
    valueHHmm,
    onChangeHHmm,
    minHHmm,
    disabled,
    emptyLabel = "Select time",
  }) {
    const [open, setOpen] = useState(false);

    // local state – null = nothing chosen yet
    const [h12, setH12] = useState(null); // number | null
    const [minute, setMinute] = useState(null); // number | null
    const [ampm, setAmpm] = useState("AM");

    // When the popup opens, initialise from existing value ONLY.
    useEffect(() => {
      if (!open) return;

      if (valueHHmm) {
        const p = to12PartsFrom24(valueHHmm);
        setH12(p.hour12);
        setMinute(p.minute);
        setAmpm(p.ampm);
      } else {
        if (minHHmm) {
          const p = to12PartsFrom24(minHHmm);
          setAmpm(p.ampm);
        } else {
          setAmpm("AM");
        }
        setH12(null);
        setMinute(null);
      }
    }, [open, valueHHmm, minHHmm]);

    const composeAndValidate = (h, m, ap) => {
      if (h == null || m == null) return false;
      const candidate = partsTo24(h, m, ap);

      if (minHHmm && candidate < minHHmm) return false;

      onChangeHHmm(candidate);
      return true;
    };

    const disabledMinute = (m) => {
      if (!minHHmm) return false;
      if (h12 == null) return false;
      const check = partsTo24(h12, m, ampm);
      return check < minHHmm;
    };

    const disabledHour = (h) => {
      if (!minHHmm) return false;
      const minsToCheck = [0, 15, 30, 45];
      return !minsToCheck.some((m) => partsTo24(h, m, ampm) >= minHHmm);
    };

    const disabledAmPm = (ap) => {
      if (!minHHmm) return false;
      const hours = h12 != null ? [h12] : [1, 12];
      const mins = minute != null ? [minute, 0, 15, 30, 45] : [0, 15, 30, 45];

      return !hours.some((h) =>
        mins.some((m) => partsTo24(h, m, ap) >= minHHmm)
      );
    };

    // Close on Escape
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const triggerLabel =
      disabled && !valueHHmm
        ? "Select date first"
        : valueHHmm
        ? to12From24(valueHHmm)
        : emptyLabel;

    return (
      <>
        {/* Trigger field */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          className={`mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-left text-[11px] focus:outline-none focus:ring-2 focus:ring-slate-300 ${
            disabled
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-white text-slate-900"
          }`}
        >
          {triggerLabel}
        </button>

        {/* Modal sheet */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            {/* Dialog */}
            <div className="relative z-50 mx-4 mb-4 w-full max-w-md rounded-2xl bg-white shadow-xl sm:mb-0">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">
                  Select time
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs font-medium text-slate-500 hover:text-slate-900"
                >
                  Close
                </button>
              </div>

              <div className="space-y-3 px-4 pt-3 pb-4">
                {/* AM / PM toggle */}
                <div className="flex gap-2">
                  {["AM", "PM"].map((k) => {
                    const selected = ampm === k;
                    const isDisabled = disabledAmPm(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          const next = k;
                          setAmpm(next);
                          if (h12 != null && minute != null) {
                            composeAndValidate(h12, minute, next);
                          }
                        }}
                        className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold ${
                          selected
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-300 bg-white text-slate-700"
                        } ${
                          isDisabled ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {k}
                      </button>
                    );
                  })}
                </div>

                {/* Hour / Minute columns */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Hours */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                      Hour
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                        const selected = h12 === h;
                        const isDisabled = disabledHour(h);
                        return (
                          <button
                            key={h}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => {
                              if (isDisabled) return;
                              const nextHour = h;
                              setH12(nextHour);
                              if (minute != null) {
                                composeAndValidate(nextHour, minute, ampm);
                              }
                            }}
                            className={`flex w-full items-center justify-between px-3 py-2 text-sm ${
                              selected ? "bg-slate-100" : "bg-white"
                            } ${
                              isDisabled ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            <span className="text-slate-900">{h}</span>
                            {selected && (
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Minutes */}
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                      Minute
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {[0, 15, 30, 45].map((m) => {
                        const selected = minute === m;
                        const isDisabled = disabledMinute(m);
                        return (
                          <button
                            key={m}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => {
                              if (isDisabled) return;
                              const nextMinute = m;
                              setMinute(nextMinute);
                              if (h12 != null) {
                                const ok = composeAndValidate(
                                  h12,
                                  nextMinute,
                                  ampm
                                );
                                if (ok) setOpen(false);
                              }
                            }}
                            className={`flex w-full items-center justify-between px-3 py-2 text-sm ${
                              selected ? "bg-slate-100" : "bg-white"
                            } ${
                              isDisabled ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            <span className="text-slate-900">
                              {pad2(m)}
                            </span>
                            {selected && (
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {minHHmm && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    Earliest: {to12From24(minHHmm)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  /* ───────── Main page ───────── */

  const ManageBookings = () => {
    const { user } = useUser();
    const location = useLocation();

    const [owner, setOwner] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [pageSize, setPageSize] = useState(25); // default 25 rows
    const vehicleSelectRef = useRef(null);
    const driverSelectRef = useRef(null);
    const [matchRequests, setMatchRequests] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [bookingRows, setBookingRows] = useState([]);
const [bookingStatsServer, setBookingStatsServer] = useState({
  requests: 0,
  todaysTrips: 0,
  scheduled: 0,
  completed: 0,
  cancelledOrMissed: 0,
});
const [totalRows, setTotalRows] = useState(0);
const [totalPagesServer, setTotalPagesServer] = useState(0);


  // when Accept modal opened from a match-request
  const [assignModalMatchId, setAssignModalMatchId] = useState(null);


    // "pending" | "upcoming" | "past"
    const [view, setView] = useState("pending");

    // only used when view === "past"
    const [historyStatusFilter, setHistoryStatusFilter] = useState("all"); // all | completed | missed | cancelled

    // vehicleFilter = "all" or registrationNumber or "id:<id>"
    const [vehicleFilter, setVehicleFilter] = useState("all");
    // driverFilter = "all" or driverId (string)
    const [driverFilter, setDriverFilter] = useState("all");

    // density: comfortable | compact
    const [density] = useState("comfortable");

    // sorting
    const [sortConfig, setSortConfig] = useState({
      key: "pickupTime",
      direction: "asc",
    });

    // pagination
    const [page, setPage] = useState(1);

    // Modals
    const [assignModalBooking, setAssignModalBooking] = useState(null);
    const [assignModalDriverId, setAssignModalDriverId] = useState("");
    const [cancelModalBooking, setCancelModalBooking] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [ownerCancelCutoffMinutes, setOwnerCancelCutoffMinutes] = useState(0);
    const [assignModalMode, setAssignModalMode] = useState("with_driver"); // with_driver | self_drive


    // Inline reassign popover for upcoming trips
    const [reassignBooking, setReassignBooking] = useState(null);
    const [reassignDriverId, setReassignDriverId] = useState("");

  // Fare breakup modal
  const [fareBooking, setFareBooking] = useState(null);


    // ─── Availability modal (block booking dates) ───
    const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
    const [availabilityVehicle, setAvailabilityVehicle] = useState(null);
    const [availabilityMonth, setAvailabilityMonth] = useState(() => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
    const [availabilityLoading, setAvailabilityLoading] = useState(false);
    const [driverBlocksById, setDriverBlocksById] = useState({}); // { [driverId]: blocks[] }
  const [driverBlocksLoading, setDriverBlocksLoading] = useState(false);

    const [availabilitySelectedDate, setAvailabilitySelectedDate] =
      useState(null); // "YYYY-MM-DD"

    const [availabilityFullDay, setAvailabilityFullDay] = useState(true);
    const [availabilityStartTime, setAvailabilityStartTime] = useState("");
    const [availabilityEndTime, setAvailabilityEndTime] = useState("");

    const buildVehicleMapByReg = (vehicles) => {
    const map = {};
    for (const v of vehicles || []) {
      const reg = v?.registrationNumber;
      if (reg) map[String(reg)] = v;
    }
    return map;
  };

    const vehicleByReg = useMemo(() => buildVehicleMapByReg(vehicles), [vehicles]);

    // NEW: when user clicks "Block booking dates" with "All vehicles" selected
    const [availabilityVehicleChooserOpen, setAvailabilityVehicleChooserOpen] =
      useState(false);
    const [availabilityVehicleChoice, setAvailabilityVehicleChoice] =
      useState("");

    const availabilityCalendarCells = useMemo(
      () => buildMonthCells(availabilityMonth),
      [availabilityMonth]
    );

    const splitISOToDateTime = (iso) => {
    if (!iso) return { date: "", time: "" };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
  };

const adaptMatchToRow = (m, vehicleByReg) => {
  const { date: pickupDate, time: pickupTime } = splitISOToDateTime(m?.pickupAt);
  const { date: dropDate, time: dropTime } = splitISOToDateTime(m?.dropAt);

  const v = vehicleByReg?.[String(m?.vehicleRegNo || "")] || null;

  return {
    _isMatch: true,
    _matchId: m?.matchId,
    _requestId: m?.requestId,

    id: `match-${m?.matchId}`,

    pickupDate,
    pickupTime,
    dropDate: dropDate || pickupDate,
    dropTime,

    // ✅ allow conflict window estimation
    durationHours: m?.durationHours ?? m?.duration ?? m?.hours ?? null,

    // ✅ if matchmaking already knows selfDrive preference
    selfDrive: m?.selfDrive === true,

    pickupLocation: m?.pickupLocation || "",
    destinations: Array.isArray(m?.destinations) ? m.destinations : [],
    destination: m?.destination || "",

    ownerFare: m?.ownerNetFare ?? (m?.basePrice+m?.fuelCost+m?.toll) ?? null,
    basePrice: m?.basePrice ?? null,
    fuelCost: m?.fuelCost ?? null,
    toll: m?.toll ?? null,

    commission: m?.commission ?? null,
    apiCharges: m?.apiCharges ?? null,
    gst: m?.gst ?? null,
    paymentStatus: m?.paymentStatus ?? null,
    paidAmount: m?.paidAmount ?? null,

    matchStatus: m?.matchStatus,
    requestStatus: m?.requestStatus,

    vehicle: v || {
      brand: "Vehicle",
      model: "",
      registrationNumber: m?.vehicleRegNo || "—",
    },

    assignedDriver: null,
    driverAction: null,
    ownerAction: null,
    status: "MATCH_REQUEST",
  };
};


    const matchRows = useMemo(() => {
    return (matchRequests || []).map((m) => adaptMatchToRow(m, vehicleByReg));
  }, [matchRequests, vehicleByReg]);



    const nowDT = new Date();
    const todayIso = toISODate(nowDT);

    /* ─── Driver availability (frontend guard) ─── */

    /* ─── Flatten bookings ─── */
   const allBookings = useMemo(() => bookingRows || [], [bookingRows]);


  const isDriverGloballyBlocked = (d) => {
    // supports multiple possible backend shapes safely
    return (
      d?.blocked === true ||
      d?.isBlocked === true ||
      String(d?.status || "").toUpperCase() === "BLOCKED" ||
      String(d?.availabilityStatus || "").toUpperCase() === "BLOCKED"
    );
  };

  const getDriverComplianceRaw = (driver) =>
  driver?.complianceStatus ||
  driver?.verificationStatus ||
  driver?.kycStatus ||
  driver?.driverCompliance?.status ||
  driver?.compliance?.status ||
  "";

const isDriverComplianceVerified = (driver) => {
  const raw = String(getDriverComplianceRaw(driver) || "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
  return raw === "VERIFIED" || raw === "APPROVED";
};

const isDriverEligibleForAssignment = (driver) => {
  if (!driver) return false;

  const explicit =
    typeof driver?.eligibleForAssignment === "boolean"
      ? driver.eligibleForAssignment
      : typeof driver?.assignmentEligible === "boolean"
      ? driver.assignmentEligible
      : typeof driver?.eligibleForBooking === "boolean"
      ? driver.eligibleForBooking
      : typeof driver?.openForBooking === "boolean"
      ? driver.openForBooking
      : null;

  if (explicit != null) return explicit && isDriverComplianceVerified(driver);
  return isDriverComplianceVerified(driver);
};

  const findDriverConflictBlock = (driverId, candidateBooking) => {
    if (!driverId || !candidateBooking) return null;

    const blocks = driverBlocksById?.[String(driverId)] || [];
    if (!blocks.length) return null;

    const candWin = getBookingWindowDT(candidateBooking);

    for (const blk of blocks) {
      const w = blockToWindowDT(blk);
      if (!w) continue;
      if (windowsOverlap(candWin, w)) return blk;
    }

    return null;
  };


  const bookingCountsForDriverBusy = (b, nowDT) => {
    if (!b?.assignedDriver?.id) return false;

    const dA = String(b?.driverAction || "").toUpperCase();
    if (dA === "DECLINED") return false;

    if (explicitCancelled(b)) return false;
    if (isCompletedStrict(b)) return false;
    if (isMissed(b, nowDT)) return false;

    return true;
  };

  const findDriverConflictBooking = (driverId, candidateBooking, allBookings, nowDT) => {
    if (!driverId || !candidateBooking) return null;

    const candWin = getBookingWindowDT(candidateBooking);

    for (const b of allBookings || []) {
      if (!b) continue;
      if (String(b.id) === String(candidateBooking.id)) continue;
      if (!bookingCountsForDriverBusy(b, nowDT)) continue;

      if (String(b.assignedDriver?.id) !== String(driverId)) continue;

      const otherWin = getBookingWindowDT(b);
      if (windowsOverlap(candWin, otherWin)) return b;
    }

    return null;
  };

  const getDriverSelectState = (driverObj, candidateBooking, allBookings, nowDT) => {
    const driverId = driverObj?.id != null ? String(driverObj.id) : "";
    if (!driverId) return { disabled: true, reason: "Invalid driver" };

    return { disabled: false, reason: "" };
  };

  const buildSelfDrivePayload = (owner, user) => {
    const name =
      owner?.displayName ||
      owner?.name ||
      user?.userName ||
      user?.name ||
      "";

    const phoneNumber =
      owner?.phoneNumber ||
      owner?.phone ||
      owner?.mobile ||
      owner?.phoneNo ||
      user?.phoneNumber ||
      user?.phone ||
      "";

    const email = owner?.email || user?.email || "";

    return {
      name: String(name || "").trim(),
      phoneNumber: String(phoneNumber || "").trim(),
      email: String(email || "").trim(),
    };
  };






  /**
   * Convert OwnerMatchRequestDTO -> "booking-like row"
   * so your table / route renderer can be reused.
   */



    /* ─── Decline helpers (use drivers state) ─── */
  const isDriverDeclined = (b) => {
    const dA = (b?.driverAction || "").toUpperCase();
    if (dA === "DECLINED") return true;
    return Array.isArray(b?.declinedDriverIds) && b.declinedDriverIds.length > 0;
  };

    const getDeclinedDriverName = (b) => {
      if (b?.lastDeclinedDriverName) return b.lastDeclinedDriverName;

      const dA = (b?.driverAction || "").toUpperCase();
      if (dA === "DECLINED" && b?.assignedDriver?.name) {
        return b.assignedDriver.name;
      }

      if (Array.isArray(b?.declinedDriverIds) && b.declinedDriverIds.length > 0) {
        const lastId = String(
          b.declinedDriverIds[b.declinedDriverIds.length - 1]
        );
        const d = drivers.find((x) => String(x.id) === lastId);
        if (d?.name) return d.name;
      }

      return null;
    };

    const getDeclinedSet = (b) => {
      if (!b) return new Set();
      const ids = [
        ...((b.declinedDriverIds || []).map((x) => String(x)) || []),
      ];
      const dA = (b.driverAction || "").toUpperCase();
      if (dA === "DECLINED" && b.assignedDriver?.id != null) {
        ids.push(String(b.assignedDriver.id));
      }
      return new Set(ids);
    };

    const handleVehicleLabelClick = () => {
      if (vehicleSelectRef.current) {
        vehicleSelectRef.current.focus();
        vehicleSelectRef.current.click();
      }
    };

    const handleDriverLabelClick = () => {
      if (driverSelectRef.current) {
        driverSelectRef.current.focus();
        driverSelectRef.current.click();
      }
    };

    const loadMatchRequests = async (ownerId) => {
    try {
      setMatchLoading(true);
      const rows = await fetchOwnerMatchRequests(ownerId);
      setMatchRequests(rows);
    } catch (e) {
      console.error(e);
      toast.error("Unable to load match requests");
      setMatchRequests([]);
    } finally {
      setMatchLoading(false);
    }
  };

  const loadOwnerBookingsPage = async ({
  pageArg = page,
  pageSizeArg = pageSize,
  viewArg = view,
  vehicleFilterArg = vehicleFilter,
  driverFilterArg = driverFilter,
  historyStatusArg = historyStatusFilter,
  sortArg = sortConfig,
} = {}) => {
  try {
    const viewParam =
      viewArg === "pending" ? "requests" : viewArg === "past" ? "history" : "scheduled";

    const vehicleRegParam =
      vehicleFilterArg && vehicleFilterArg !== "all" && !String(vehicleFilterArg).startsWith("id:")
        ? vehicleFilterArg
        : undefined;

    const res = await fetchOwnerBookingPage({
      view: viewParam,
      vehicleReg: vehicleRegParam,
      driverId: driverFilterArg,
      historyStatus: historyStatusArg,
      page: Math.max(0, (pageArg || 1) - 1),
      size: pageSizeArg,
      sortKey: sortArg?.key || "date",
      sortDir: sortArg?.direction || "desc",
    });

    const rows = Array.isArray(res?.items) ? applyLedgerToList(res.items) : [];
    setBookingRows(rows);
    setTotalRows(Number(res?.totalElements || 0));
    setTotalPagesServer(Number(res?.totalPages || 0));
  } catch (err) {
    console.error(err);
    toast.error("Unable to load bookings");
    setBookingRows([]);
    setTotalRows(0);
    setTotalPagesServer(0);
  }
};



    const getBookingWindowDT = (b) => {
    const start = toDT(b?.pickupDate, b?.pickupTime);

    // end: prefer explicit drop date+time, else durationHours, else +1h
    const dropDateRaw = b?.dropDate || b?.pickupDate;
    let end = null;

    if (dropDateRaw && b?.dropTime) {
      end = toDT(dropDateRaw, b.dropTime);
    } else if (b?.durationHours != null && !Number.isNaN(Number(b.durationHours))) {
      end = new Date(start.getTime() + Number(b.durationHours) * 60 * 60 * 1000);
    } else {
      end = new Date(start.getTime() + 60 * 60 * 1000);
    }

    // overnight safety (end earlier than start -> add a day)
    if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);

    return { start, end };
  };

  const windowsOverlap = (A, B) => {
    // strict overlap: [start,end)
    return A.start < B.end && B.start < A.end;
  };

  const blockToWindowDT = (blk) => {
    // Preferred backend shape: startAt/endAt ISO strings
    if (blk?.startAt && blk?.endAt) {
      const start = toDateSafe(blk.startAt);
      const end = toDateSafe(blk.endAt);
      if (start && end) return { start, end };
    }

    // Fallback shape: startDate/endDate + startTime/endTime + fullDay
    if (blk?.startDate) {
      const startTime = blk.fullDay ? "00:00" : (blk.startTime || "00:00");
      const endDate = blk.endDate || blk.startDate;

      // endTime: if fullDay, treat as next day 00:00 (end-exclusive)
      if (blk.fullDay) {
        const start = toDT(blk.startDate, "00:00");
        const end = new Date(toDT(endDate, "00:00").getTime() + 24 * 60 * 60 * 1000);
        return { start, end };
      }

      const endTime = blk.endTime || "23:59";
      let start = toDT(blk.startDate, startTime);
      let end = toDT(endDate, endTime);

      if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      return { start, end };
    }

    return null;
  };

  const phoneLast10 = (v) =>
    String(v || "").replace(/\D/g, "").slice(-10);

  const getOwnerPhoneLast10 = () =>
    phoneLast10(
      owner?.phoneNumber ||
        owner?.phone ||
        owner?.mobile ||
        owner?.phoneNo ||
        user?.phoneNumber ||
        user?.phone ||
        ""
    );

  // This is the “self-drive profile” driver (owner-as-driver) if it already exists in your drivers list.
  const selfDriveDriver = useMemo(() => {
    const p = getOwnerPhoneLast10();
    if (!p) return null;
    return (drivers || []).find((d) => phoneLast10(d?.phoneNumber) === p) || null;
  }, [drivers, owner, user]);

  const getSelfDriveSelectState = (candidateBooking, allBookings, nowDT) => {
    if (!candidateBooking) return { disabled: false, reason: "" };

    // If profile doesn't exist yet, backend will create it → don't block self-drive in UI.
    // (We only disable when we can prove conflict/blocked on an existing profile.)
    if (!selfDriveDriver?.id) return { disabled: false, reason: "" };

    return { disabled: false, reason: "" };
  };



  const selfDriveState = useMemo(() => {
    if (!assignModalBooking) return { disabled: false, reason: "" };
    return getSelfDriveSelectState(assignModalBooking, allBookings, new Date());
  }, [assignModalBooking, selfDriveDriver, driverBlocksById, allBookings]);

    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const config = await fetchOwnerBookingCancelConfig();
          if (!alive) return;
          const next = Number(config?.ownerCancelBeforePickupMinutes);
          if (Number.isFinite(next)) setOwnerCancelCutoffMinutes(Math.max(0, Math.floor(next)));
        } catch (err) {
          console.warn("Unable to load owner booking cancel config", err);
        }
      })();
      return () => {
        alive = false;
      };
    }, []);








    /* ─── Owner profile ─── */
    useEffect(() => {
      (async () => {
        try {
          const prof = await fetchOwnerProfile();
          setOwner(prof);
        } catch (err) {
          console.error(err);
          toast.error("Unable to load owner profile");
        }
      })();
    }, []);

    /* ─── Vehicles + bookings + drivers ─── */
 useEffect(() => {
  if (!owner?.id) return;

  (async () => {
    try {
      setLoading(true);
      flushQueueToLedger();

      const [rawVehicles, driverList, stats] = await Promise.all([
        fetchOwnerVehiclesWithBookings(owner.id),
        fetchOwnerDrivers(owner.id),
        fetchOwnerBookingStats(),
      ]);

      setVehicles(Array.isArray(rawVehicles) ? rawVehicles : []);
      setDrivers(Array.isArray(driverList) ? driverList : []);
      setBookingStatsServer(stats || {
        requests: 0,
        todaysTrips: 0,
        scheduled: 0,
        completed: 0,
        cancelledOrMissed: 0,
      });

      await Promise.all([
        loadMatchRequests(owner.id),
        loadOwnerBookingsPage({ pageArg: 1 }),
      ]);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load bookings");
    } finally {
      setLoading(false);
    }
  })();
}, [owner]);

useEffect(() => {
  if (!owner?.id) return;
  if (view === "pending") return; // pending tab still uses match requests

  loadOwnerBookingsPage();
}, [
  owner?.id,
  view,
  vehicleFilter,
  driverFilter,
  historyStatusFilter,
  sortConfig,
  page,
  pageSize,
]);





    /* ─── Apply vehicle filter from URL (?car=REGNO) ─── */
    useEffect(() => {
      if (!vehicles.length) return;

      try {
        const params = new URLSearchParams(location.search || "");
        const carParam = params.get("car");
        if (!carParam) return;

        const reg = carParam;

        const hasVehicle = vehicles.some(
          (v) => v.registrationNumber && v.registrationNumber === reg
        );

        if (hasVehicle) {
          setVehicleFilter(reg);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to apply vehicle filter from URL:", err);
      }
    }, [location.search, vehicles]);

    /* ─── Vehicle filter options ─── */
    const vehicleOptions = useMemo(() => {
      const base = [
        {
          value: "all",
          label: "All vehicles",
        },
      ];
      const vs = vehicles.map((v) => {
        const reg = v.registrationNumber || "";
        const label = `${v.brand || "Vehicle"} ${
          v.model || ""
        } · ${reg || "No reg"}`;
        const value = reg || `id:${v.id}`;
        return { value, label };
      });
      return [...base, ...vs];
    }, [vehicles]);





    const belongsToVehicleFilter = (booking, filterValue) => {
      if (filterValue === "all") return true;
      const reg = booking.vehicle?.registrationNumber || "";
      if (reg && reg === filterValue) return true;
      if (filterValue.startsWith("id:")) {
        const id = filterValue.slice(3);
        return String(booking.vehicle?.id) === id;
      }
      return false;
    };

    const findVehicleByValue = (value, list) => {
      if (!value || value === "all") return null;
      return (
        (list || []).find((v) => {
          const reg = v.registrationNumber || "";
          if (reg && reg === value) return true;
          if (value.startsWith("id:")) {
            const id = value.slice(3);
            return String(v.id) === id;
          }
          return false;
        }) || null
      );
    };

    const activeVehicleForFilter = useMemo(
      () => findVehicleByValue(vehicleFilter, vehicles),
      [vehicleFilter, vehicles]
    );

    /* ─── Stats ─── */
const bookingStats = useMemo(() => {
  return {
    total: totalRows,
    pending: Number(matchRequests?.length ?? bookingStatsServer?.requests ?? 0),
    upcoming: Number(bookingStatsServer?.scheduled ?? 0),
    cancelled: Number(bookingStatsServer?.cancelledOrMissed ?? 0),
    completed: Number(bookingStatsServer?.completed ?? 0),
    todaysTrips: Number(bookingStatsServer?.todaysTrips ?? 0),
    todaysActiveVehicleCount: 0,
  };
}, [totalRows, matchRequests, bookingStatsServer]);



    /* ─── Reset page when filters/sort change ─── */
    useEffect(() => {
      setPage(1);
    }, [view, vehicleFilter, driverFilter, historyStatusFilter, sortConfig, pageSize]);

    // Close reassign/fare popovers when view changes
  useEffect(() => {
    setReassignBooking(null);
    setReassignDriverId("");
    setFareBooking(null);
  }, [view]);


  useEffect(() => {
    if (!assignModalBooking || assignModalMode !== "with_driver" || !assignModalDriverId) return;

    const d = drivers.find((x) => String(x.id) === String(assignModalDriverId));
    if (!d) return;

    const st = getDriverSelectState(d, assignModalBooking, allBookings, nowDT);
    if (st.disabled) {
      setAssignModalDriverId("");
      toast.error(`Selected driver unavailable: ${st.reason}`);
    }
  }, [assignModalBooking, assignModalMode, assignModalDriverId, drivers, allBookings]);

  useEffect(() => {
    if (!assignModalBooking) return;
    if (assignModalMode !== "self_drive") return;

    const st = getSelfDriveSelectState(assignModalBooking, allBookings, new Date());
    if (st.disabled) {
      setAssignModalMode("with_driver");
      toast.error(`Self drive unavailable: ${st.reason}`);
    }
  }, [assignModalBooking, assignModalMode, selfDriveDriver, driverBlocksById, allBookings]);



    /* ─── Filtered + sorted list ─── */
    const sourceRows = view === "pending" ? matchRows : allBookings;
  let list = sourceRows.filter((b) => belongsToVehicleFilter(b, vehicleFilter));

const filteredBookings = useMemo(() => {
  if (view === "pending") {
    return matchRows.filter((b) => belongsToVehicleFilter(b, vehicleFilter));
  }
  return allBookings;
}, [view, matchRows, vehicleFilter, allBookings]);


    /* ─── Pagination ─── */
  const totalPages = useMemo(() => {
  if (view === "pending") {
    return Math.max(1, Math.ceil(filteredBookings.length / pageSize));
  }
  return Math.max(1, totalPagesServer || 1);
}, [view, filteredBookings.length, pageSize, totalPagesServer]);

const paginatedBookings = useMemo(() => {
  if (view === "pending") {
    const start = (page - 1) * pageSize;
    return filteredBookings.slice(start, start + pageSize);
  }
  return filteredBookings;
}, [view, filteredBookings, page, pageSize]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    setFareBooking(null);
  };


    /* ─── Refresh helper after actions ─── */
  const refetchBookings = async (ownerId) => {
  try {
    if (ownerId) {
      const stats = await fetchOwnerBookingStats();
      setBookingStatsServer(stats || {
        requests: 0,
        todaysTrips: 0,
        scheduled: 0,
        completed: 0,
        cancelledOrMissed: 0,
      });
    }

    if (view !== "pending") {
      await loadOwnerBookingsPage();
    }
  } catch (err) {
    console.error(err);
    toast.error("Unable to refresh bookings");
  }
};
    /* ─── Availability helpers: block booking dates ─── */

    const fetchSingleDriverBlocks = async (driverId, bookingForChecks) => {
  if (!driverId || !bookingForChecks) return [];

  const { start, end } = getBookingWindowDT(bookingForChecks);
  const windowStartISO = start.toISOString();
  const windowEndISO = end.toISOString();

  try {
    const blocks = await fetchDriverAvailabilityBlocks(driverId, windowStartISO, windowEndISO);
    return Array.isArray(blocks) ? blocks : [];
  } catch {
    return [];
  }
};

    const loadMonthBlocks = async (vehicle, monthDate) => {
      if (!vehicle?.registrationNumber) return;
      try {
        setAvailabilityLoading(true);
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        const from = toISODate(monthStart);
        const to = toISODate(monthEnd);

        const data = await fetchAvailabilityBlocks(
          vehicle.registrationNumber,
          from,
          to
        );
        setAvailabilityBlocks(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        toast.error("Unable to load blocked dates");
      } finally {
        setAvailabilityLoading(false);
      }
    };

    const openAvailabilityForVehicle = (vehicle) => {
      if (!vehicle?.registrationNumber) {
        toast.error("Vehicle registration is missing.");
        return;
      }

      const todayIsoLocal = toISODate(new Date());

      setAvailabilityVehicle(vehicle);
      setAvailabilityModalOpen(true);
      setAvailabilitySelectedDate(todayIsoLocal);
      setAvailabilityFullDay(true);
      setAvailabilityStartTime("");
      setAvailabilityEndTime("");

      loadMonthBlocks(vehicle, availabilityMonth);
    };

    const handleOpenAvailability = () => {
      if (!vehicles.length) {
        toast.error("Add a vehicle first to manage booking dates.");
        return;
      }

      // If a specific vehicle is already selected in the filter, go straight in
      if (activeVehicleForFilter) {
        openAvailabilityForVehicle(activeVehicleForFilter);
        return;
      }

      // No specific vehicle selected ("All vehicles") → show chooser sheet
      const first = vehicles[0];
      const defaultValue =
        first?.registrationNumber || (first?.id != null ? `id:${first.id}` : "");
      setAvailabilityVehicleChoice(defaultValue);
      setAvailabilityVehicleChooserOpen(true);
    };

    const handleConfirmAvailabilityVehicle = () => {
      if (!availabilityVehicleChoice) {
        toast.error("Select a vehicle to continue.");
        return;
      }

      const v = findVehicleByValue(availabilityVehicleChoice, vehicles);
      if (!v) {
        toast.error("Selected vehicle not found.");
        return;
      }

      setAvailabilityVehicleChooserOpen(false);
      openAvailabilityForVehicle(v);
    };

    const handleCloseAvailability = () => {
      setAvailabilityModalOpen(false);
      setAvailabilityVehicle(null);
      setAvailabilityBlocks([]);
      setAvailabilitySelectedDate(null);
    };

    const handleMonthChange = (direction) => {
      if (!availabilityVehicle) return;
      setAvailabilityBlocks([]);
      setAvailabilitySelectedDate(null);

      setAvailabilityMonth((prev) => {
        const next = new Date(prev);
        next.setMonth(prev.getMonth() + direction);
        loadMonthBlocks(availabilityVehicle, next);
        return next;
      });
    };

    const handleDayClick = (cell) => {
      if (!cell.inMonth) return;
      if (cell.iso < todayIso) return;

      setAvailabilitySelectedDate(cell.iso);
      setAvailabilityFullDay(true);
      setAvailabilityStartTime("");
      setAvailabilityEndTime("");
    };

    const isDateBlocked = (iso) =>
      availabilityBlocks.some((blk) =>
        isISOWithin(iso, blk.startDate, blk.endDate)
      );

    const blocksForSelectedDate = useMemo(() => {
      if (!availabilitySelectedDate) return [];
      return availabilityBlocks.filter((blk) =>
        isISOWithin(availabilitySelectedDate, blk.startDate, blk.endDate)
      );
    }, [availabilityBlocks, availabilitySelectedDate]);

  




    const handleCreateAvailabilityBlock = async () => {
      if (!availabilityVehicle?.registrationNumber) return;
      if (!availabilitySelectedDate) {
        toast.error("Select a date on the calendar first.");
        return;
      }

      if (availabilitySelectedDate < todayIso) {
        toast.error("You cannot block dates in the past.");
        return;
      }

      if (
        !availabilityFullDay &&
        (!availabilityStartTime || !availabilityEndTime)
      ) {
        toast.error("Select start and end time, or choose full day.");
        return;
      }

      if (!availabilityFullDay) {
        if (availabilityStartTime >= availabilityEndTime) {
          toast.error("End time must be after start time.");
          return;
        }
      }

      try {
        const payload = {
          startDate: availabilitySelectedDate,
          endDate: availabilitySelectedDate,
          fullDay: availabilityFullDay,
          startTime: availabilityFullDay ? null : availabilityStartTime,
          endTime: availabilityFullDay ? null : availabilityEndTime,
        };

        await createAvailabilityBlock(
          availabilityVehicle.registrationNumber,
          payload
        );

        toast.success("Date blocked for new bookings.");

        await loadMonthBlocks(availabilityVehicle, availabilityMonth);

        if (!availabilityFullDay) {
          setAvailabilityStartTime("");
          setAvailabilityEndTime("");
        }
      } catch (err) {
        console.error(err);
        toast.error("Unable to block this date.");
      }
    };

    const handleDeleteAvailabilityBlock = async (blockId) => {
      try {
        await deleteAvailabilityBlock(blockId);
        setAvailabilityBlocks((prev) => prev.filter((b) => b.id !== blockId));
        toast.success("Opened for bookings.");
      } catch (err) {
        console.error(err);
        toast.error("Unable to open this slot.");
      }
    };

    

    /* ─── Actions ─── */

  const openAssignModal = (row) => {
    setAssignModalBooking(row);

    // ✅ if opened from matchmaking request
    setAssignModalMatchId(row?._isMatch ? row?._matchId : null);

    const selfDrive = isSelfDriveBooking(row);
    setAssignModalMode(selfDrive ? "self_drive" : "with_driver");
    setAssignModalDriverId(selfDrive ? "" : row?.assignedDriver?.id || "");
  };



const handleConfirmAssignAndAccept = async () => {
  try {
    if (!assignModalBooking) return;

    const ownerId = owner?.id;
    if (!ownerId) {
      toast.error("Owner not loaded. Please refresh.");
      return;
    }

    const now = new Date();
    const isMatchRow = !!assignModalBooking?._isMatch;
    const matchId = assignModalMatchId ?? assignModalBooking?._matchId ?? null;

    const getSelectedDriver = (driverIdLike) => {
      const idStr = String(driverIdLike ?? "");
      return (drivers || []).find((d) => String(d?.id) === idStr) || null;
    };

    const validateDriverSelection = async () => {
      const selectedDriverId = assignModalDriverId;
      if (!selectedDriverId) {
        toast.error("Select a driver first.");
        return { ok: false };
      }

      const selectedDriver = getSelectedDriver(selectedDriverId);
      if (!selectedDriver) {
        toast.error("Selected driver not found.");
        return { ok: false };
      }

      const st = getDriverSelectState(selectedDriver, assignModalBooking, allBookings, now);
      if (st?.disabled) {
        toast.error(`Selected driver unavailable: ${st.reason}`);
        return { ok: false };
      }

      const driverIdNum = Number(selectedDriverId);
      if (!Number.isFinite(driverIdNum)) {
        toast.error("Invalid driver id.");
        return { ok: false };
      }

      return { ok: true, driverIdNum };
    };

    const validateSelfDrive = () => {
      const st = getSelfDriveSelectState(assignModalBooking, allBookings, now);
      if (st?.disabled) {
        toast.error(`Self drive unavailable: ${st.reason}`);
        return { ok: false };
      }

      const payload = buildSelfDrivePayload(owner, user);
      if (!payload?.phoneNumber) {
        toast.error("Owner phone number missing — cannot self-drive.");
        return { ok: false };
      }
      if (!payload?.name) {
        toast.error("Owner name missing — please complete owner profile first.");
        return { ok: false };
      }

      return { ok: true, payload };
    };

    if (isMatchRow) {
      if (!matchId) {
        toast.error("Match request id missing.");
        return;
      }

      if (assignModalMode === "self_drive") {
        const v = validateSelfDrive();
        if (!v.ok) return;

        await acceptOwnerMatchRequest(ownerId, matchId, {
          selfDrive: true,
          driverId: null,
          selfDriver: v.payload,
        });

        toast.success("Request accepted (Self drive)");
      } else {
        const v = await validateDriverSelection();
        if (!v.ok) return;

        await acceptOwnerMatchRequest(ownerId, matchId, {
          selfDrive: false,
          driverId: v.driverIdNum,
        });

        toast.success("Request accepted & driver assigned");
      }
    } else {
      const bookingId = assignModalBooking?.id;
      if (!bookingId) {
        toast.error("Booking id missing.");
        return;
      }

      if (assignModalMode === "self_drive") {
        const v = validateSelfDrive();
        if (!v.ok) return;

        await selfDriveAssignAndAcceptBooking(bookingId, v.payload);
        toast.success("Booking accepted (Self drive)");
      } else {
        const v = await validateDriverSelection();
        if (!v.ok) return;

        await assignDriverAndAcceptBooking(bookingId, String(v.driverIdNum));
        toast.success("Booking accepted & driver assigned");
      }
    }

    setAssignModalBooking(null);
    setAssignModalDriverId("");
    setAssignModalMode("with_driver");
    setAssignModalMatchId(null);

    try { await loadMatchRequests(ownerId); } catch {}
    try { await refetchBookings(ownerId); } catch {}
    try {
      const driverList = await fetchOwnerDrivers(ownerId);
      setDrivers(driverList || []);
    } catch {}
  } catch (err) {
    console.error(err);
    toast.error("Failed to accept");
  }
};



 





    const openCancelModal = (booking) => {
      setCancelModalBooking(booking);
      setCancelReason("");
    };

  const handleConfirmCancel = async () => {
    try {
      if (!cancelModalBooking) return;

      // ✅ match request decline
      if (cancelModalBooking?._isMatch && cancelModalBooking?._matchId) {
        await declineOwnerMatchRequest(owner?.id, cancelModalBooking._matchId, cancelReason);
        toast.success("Request declined");
        setCancelModalBooking(null);
        setCancelReason("");
        if (owner?.id) await loadMatchRequests(owner.id);
        return;
      }

      // legacy booking cancel (Scheduled tab)
      await cancelBooking(cancelModalBooking.id, cancelReason);
      toast.success("Booking cancelled");
      setCancelModalBooking(null);
      setCancelReason("");
      if (owner?.id) await refetchBookings(owner.id);
    } catch (err) {
      console.error(err);
      toast.error("Cancel failed");
    } 
  };


  // Reassign popover actions
  const openReassignPopover = (booking) => {
    setReassignBooking(booking);
    setReassignDriverId(booking?.assignedDriver?.id || "");
    // Close fare modal (use the new state setter)
    setFareBooking(null);
  };


    const closeReassignPopover = () => {
      setReassignBooking(null);
      setReassignDriverId("");
    };

const handleConfirmReassign = async () => {
  try {
    if (!reassignBooking || !reassignDriverId) return;

    if (isOwnerConfirmed(reassignBooking)) {
      await assignDriverOnly(reassignBooking.id, reassignDriverId);
    } else {
      await assignDriverAndAcceptBooking(reassignBooking.id, reassignDriverId);
    }

    toast.success("Driver updated for this booking");
    closeReassignPopover();

    if (owner?.id) await refetchBookings(owner.id);
  } catch (err) {
    console.error(err);
    toast.error("Failed to update driver");
  }
};



    const ownerName =
      owner?.displayName ||
      owner?.name ||
      user?.userName ||
      user?.name ||
      "Owner";

    /* ─── Helpers for UI ─── */
    const rowPaddingClass = density === "compact" ? "py-2" : "py-3";

    const onSortClick = (key) => {
      setSortConfig((prev) => {
        if (prev.key === key) {
          return {
            key,
            direction: prev.direction === "asc" ? "desc" : "asc",
          };
        }
        return { key, direction: "asc" };
      });
    };

    const renderSortLabel = (label, key) => {
      const active = sortConfig.key === key;
      const dir = sortConfig.direction;
      return (
        <button
          type="button"
          onClick={() => onSortClick(key)}
          className={`inline-flex items-center gap-1 ${
            active ? "text-slate-900" : "text-slate-700"
          }`}
        >
          <span>{label}</span>
          {active && (
            <span className="text-[9px]">{dir === "asc" ? "▲" : "▼"}</span>
          )}
        </button>
      );
    };

    // Route cell with vertical bullets
    const renderRouteCell = (b) => {
      const stops = Array.isArray(b.destinations)
        ? b.destinations.filter((s) => !!s)
        : [];

      const points = [
        {
          key: "pickup",
          label: "Pickup",
          text: b.pickupLocation || "—",
          color: "bg-emerald-600",
        },
        ...stops.map((s, i) => ({
          key: `stop-${i}`,
          label: `Stop ${i + 1}`,
          text: s,
          color: "bg-slate-400",
        })),
        {
          key: "drop",
          label: "Drop",
          text: b.destination || "—",
          color: "bg-rose-500",
        },
      ];

      return (
        <div className="flex flex-col gap-1 text-[12px] text-slate-800">
          {points.map((p, idx) => (
            <div key={p.key} className="flex gap-2">
              <div className="flex flex-col items-center">
                <div className={`mt-[3px] h-2 w-2 rounded-full ${p.color}`} />
                {idx !== points.length - 1 && (
                  <div className="mt-[2px] h-4 border-l border-slate-300" />
                )}
              </div>
              <div className="leading-snug">
                <span className="font-medium text-slate-600">
                  {p.label}:{" "}
                </span>
                {p.text || "—"}
              </div>
            </div>
          ))}
        </div>
      );
    };

  const renderFareCell = (b) => {
    const ownerFare =
      typeof b.ownerFare === "number" ? b.ownerFare : b.ownerFare ?? null;
    const base =
      typeof b.basePrice === "number" ? b.basePrice : b.basePrice ?? null;
    const fuel =
      typeof b.fuelCost === "number" ? b.fuelCost : b.fuelCost ?? null;
    const toll =
      typeof b.toll === "number" ? b.toll : b.toll ?? null;

    const hasAny =
      ownerFare != null || base != null || fuel != null || toll != null;

    if (!hasAny) {
      return <span className="text-[11px] text-slate-400">—</span>;
    }

    const formatMoney = (val) =>
      typeof val === "number"
        ? `₹${val.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
        : "—";

    const isOpen = fareBooking?.id === b.id;

    return (
      <div className="inline-flex items-center gap-1">
        <span
          className={`rounded-[6px] px-2 py-[2px] text-[11px] font-semibold tabular-nums ${
            isOpen
              ? "bg-slate-900 text-white ring-1 ring-slate-900"
              : "bg-slate-900 text-white"
          }`}
        >
          {formatMoney(ownerFare)}
        </span>
        <button
          type="button"
          onClick={() => setFareBooking(b)}
          className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-semibold text-slate-500 hover:border-slate-900 hover:text-slate-900"
          title="View fare breakup"
        >
          i
        </button>
      </div>
    );
  };


    /* ───────── Render ───────── */

    return (
      <div className="min-h-screen bg-white text-slate-900">
        <Header />

        <main className="bg-white">
          {/* ───── Top strip: summary ───── */}
        {/* ───── Top strip: summary ───── */}
<section className="border-b border-slate-100 bg-white">
  <div className="mx-auto max-w-[1560px] px-4 py-6 sm:px-6 md:py-7 xl:px-10 2xl:px-12">
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Fleet operations
        </p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-[1.7rem]">
          Manage bookings
        </h1>
        <p className="mt-1 text-[12px] text-slate-500">
          {ownerName} · owner console
        </p>
      </div>

      {/* Snapshot strip */}
      <div className="grid w-full grid-cols-3 gap-2 md:w-auto md:min-w-[460px] md:gap-3">
        <div className="rounded-2xl border border-[#FFE4F1] bg-white px-3 py-3 shadow-sm">
          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-600">
            Requests
          </div>
          <div className="mt-1 text-[10px] leading-snug text-slate-500">
            Need your action
          </div>
          <div className="mt-2 text-lg font-semibold tabular-nums text-amber-700">
            {bookingStats.pending}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-600">
            Today&apos;s trips
          </div>
          <div className="mt-1 text-[10px] leading-snug text-slate-500">
            Confirmed for today
          </div>
          <div className="mt-2 text-lg font-semibold tabular-nums text-slate-900">
            {bookingStats.todaysTrips}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-600">
            Scheduled
          </div>
          <div className="mt-1 text-[10px] leading-snug text-slate-500">
            Confirmed upcoming
          </div>
          <div className="mt-2 text-lg font-semibold tabular-nums text-indigo-700">
            {bookingStats.upcoming}
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

          {/* ───── Main content ───── */}
          <section className="mx-auto max-w-[1560px] px-4 pt-4 pb-12 sm:px-6 xl:px-10 2xl:px-12">
            {/* View selector + filters */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              {/* Segmented control */}
              <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50/70 p-1">
                {[
                  {
                    key: "pending",
                    label: "Requests",
                    count: bookingStats.pending,
                  },
                  {
                    key: "upcoming",
                    label: "Scheduled",
                    count: bookingStats.upcoming,
                  },
                  {
                    key: "past",
                    label: "History",
                    count: bookingStats.completed + bookingStats.cancelled,
                  },
                ].map((tab) => {
                  const active = view === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setView(tab.key)}
                      className={[
                        "flex items-center gap-2 rounded-lg px-4 py-1.5 text-xs font-medium transition-colors",
                        active
                          ? "bg-black text-white shadow-sm"
                          : "text-slate-700 hover:bg-white",
                      ].join(" ")}
                    >
                      <span>{tab.label}</span>
                      <span
                        className={[
                          "inline-flex h-[18px] min-w-[24px] items-center justify-center rounded-md border px-1.5 text-[11px] tabular-nums",
                          active
                            ? "border-white/40 bg-white/10 text-white"
                            : "border-slate-200 bg-white text-slate-700",
                        ].join(" ")}
                      >
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Right side filters */}
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
                {/* Vehicle filter */}
                <div className="inline-flex flex-1 min-w-[220px] items-stretch rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs shadow-sm transition-colors hover:border-slate-900 focus-within:border-slate-900 focus-within:ring-2 focus-within:ring-slate-900/15">
                  <button
                    type="button"
                    onClick={handleVehicleLabelClick}
                    className="flex cursor-pointer items-center pr-3 text-left"
                  >
                    <span className="text-[12px] font-medium text-slate-700">
                      Vehicle
                    </span>
                  </button>

                  <div className="my-1 w-px bg-slate-200" />

                  <div className="relative ml-3 flex flex-1 items-center">
                    <select
                      id="vehicle-filter"
                      ref={vehicleSelectRef}
                      value={vehicleFilter}
                      onChange={(e) => setVehicleFilter(e.target.value)}
                      className="w-full cursor-pointer appearance-none bg-transparent pr-6 text-[13px] font-semibold text-slate-900 focus:outline-none focus:ring-0"
                    >
                      {vehicleOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-0 flex h-full items-center text-[11px] text-slate-400">
                      ▾
                    </span>
                  </div>
                </div>

                {/* Driver filter – ONLY for Scheduled & History */}
                {view !== "pending" && (
                  <div className="inline-flex flex-1 min-w-[220px] items-stretch rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs shadow-sm transition-colors hover:border-slate-900 focus-within:border-slate-900 focus-within:ring-2 focus-within:ring-slate-900/15">
                    <button
                      type="button"
                      onClick={handleDriverLabelClick}
                      className="flex cursor-pointer items-center pr-3 text-left"
                    >
                      <span className="text-[12px] font-medium text-slate-700">
                        Driver
                      </span>
                    </button>

                    <div className="my-1 w-px bg-slate-200" />

                    <div className="relative ml-3 flex flex-1 items-center">
                      <select
                        id="driver-filter"
                        ref={driverSelectRef}
                        value={driverFilter}
                        onChange={(e) => setDriverFilter(e.target.value)}
                        className="w-full cursor-pointer appearance-none bg-transparent pr-6 text-[13px] font-semibold text-slate-900 focus:outline-none focus:ring-0"
                      >
                        <option value="all">All drivers</option>
                        <option value="self">Self drive</option>
                        {drivers.map((d) => (
                          <option key={d.id} value={String(d.id)}>
                            {d.name}
                            {d.phoneNumber ? ` (${d.phoneNumber})` : ""}
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-0 flex h-full items-center text-[11px] text-slate-400">
                        ▾
                      </span>
                    </div>
                  </div>
                )}

                {/* Block booking dates button */}
                <button
                  type="button"
                  onClick={handleOpenAvailability}
                  disabled={!vehicles.length}
                  className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold text-slate-800 shadow-sm hover:border-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"

                >
                  <span className="mr-1.5 text-[13px]" aria-hidden>
                    📅
                  </span>
                  <span>Block booking dates</span>
                </button>
              </div>
            </div>

            {/* Extra status filter inside History */}
            {view === "past" && (
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {[
                  { key: "all", label: "All" },
                  { key: "completed", label: "Completed" },
                  { key: "missed", label: "Missed" },
                  { key: "cancelled", label: "Cancelled" },
                ].map((chip) => {
                  const active = historyStatusFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setHistoryStatusFilter(chip.key)}
                      className={[
                        "inline-flex items-center justify-center rounded-lg border px-3.5 py-1.5 text-[11px] font-medium transition-colors",
                        active
                          ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Loading / empty overall */}
            {loading && (
              <div className="mt-8 text-center text-xs sm:text-sm text-slate-500">
                Loading bookings…
              </div>
            )}

            {!loading && allBookings.length === 0 && (
              <div className="mt-10 rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 px-5 py-8 text-center text-sm text-slate-500">
                No bookings yet. Once users start planning trips with your
                vehicles, they will appear here.
              </div>
            )}

          {!loading && (allBookings.length > 0 || matchRows.length > 0) && (
              <div className="mt-5 rounded-3xl border border-slate-200 bg-white shadow-sm">
                {/* Header strip for current view */}
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {view === "pending"
                      ? "Booking requests"
                      : view === "upcoming"
                      ? "Upcoming trips"
                      : "Past & cancelled"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Showing {filteredBookings.length} booking
                    {filteredBookings.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs sm:text-[13px]">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          #
                        </th>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          {renderSortLabel("Vehicle", "vehicle")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          {renderSortLabel("Schedule", "pickupTime")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          Route
                        </th>
                        {view !== "pending" && (
                          <th className="px-4 py-3 text-left font-medium sm:px-5">
                            Driver
                          </th>
                        )}
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          {renderSortLabel("Fare", "fare")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          {renderSortLabel("Status", "status")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium sm:px-5">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedBookings.length === 0 && (
                        <tr>
                          <td
                            colSpan={view === "pending" ? 7 : 8}
                            className="px-4 py-8 text-center text-xs sm:text-sm text-slate-500 sm:px-5"
                          >
                            No bookings in this view.
                          </td>
                        </tr>
                      )}

	                      {paginatedBookings.map((b, idx) => {
	                        const globalIndex = (page - 1) * pageSize + idx + 1;

	                        const actualPickupDt = toDT(b.pickupDate, b.pickupTime);
	                        const pickupDateLabel = fmtDateOnly(b.pickupDate);
	                        const dropDateRaw = b.dropDate || b.pickupDate;
	                        const pickupTimeLabel = fmtTimeOnly12h(
	                          b.pickupDate,
                          b.pickupTime
                        );
                        const dropDateLabel = fmtDateOnly(dropDateRaw);
                        const dropTimeLabel = fmtTimeOnly12h(
                          dropDateRaw,
                          b.dropTime
                        );

                        const showPendingActions =
                          view === "pending" &&
                          isPendingOwnerDecision(b) &&
                          !isMissed(b, nowDT);

                        const showUpcomingCancel =
                          view === "upcoming" &&
                          !explicitCancelled(b) &&
                          !isCompletedStrict(b) &&
                          !isMissed(b, nowDT) &&
                          !!actualPickupDt &&
                          actualPickupDt.getTime() - nowDT.getTime() > Math.max(0, ownerCancelCutoffMinutes) * 60 * 1000;

                        const driverHasDeclined = isDriverDeclined(b);
                        const declinedName = getDeclinedDriverName(b);

                        return (
                          <tr
                            key={b.id || `${b.pickupDate}-${idx}`}
                            className="border-t border-slate-100 hover:bg-slate-50/70"
                          >
                            {/* # */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600 tabular-nums">
                                {globalIndex}
                              </span>
                            </td>

                            {/* Vehicle */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              <div className="text-sm font-semibold text-slate-900">
                                {b.vehicle?.brand} {b.vehicle?.model}
                              </div>
                              <div className="mt-1 inline-flex items-center rounded-[6px] bg-slate-100 px-2 py-[2px] text-[11px] font-medium text-slate-700">
                                {b.vehicle?.registrationNumber || "No reg"}
                              </div>
                            </td>

                            {/* Schedule */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              <div className="flex flex-col gap-1 text-[12px] text-slate-800">
                                <div>
                                  <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                    Pickup
                                  </div>
                                  <div className="text-[13px] text-slate-900">
                                    {pickupDateLabel}
                                  </div>
                                  <div className="text-[12px] text-slate-700">
                                    {pickupTimeLabel}
                                  </div>
                                </div>

                                <div className="my-1 h-px bg-slate-100" />

                                <div>
                                  <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                    Drop
                                  </div>
                                  <div className="text-[13px] text-slate-900">
                                    {dropDateLabel}
                                  </div>
                                  <div className="text-[12px] text-slate-700">
                                    {dropTimeLabel}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Route */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              {renderRouteCell(b)}
                            </td>

                            {/* Driver (upcoming/history) */}
                            {view !== "pending" && (
                              <td
                                className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                              >
                                <div className="relative">
                                  {view === "upcoming" ? (
                                    <>
                                  <button
    type="button"
    onClick={() => {
      if (isSelfDriveBooking(b)) return;
      openReassignPopover(b);
    }}
    className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] font-medium text-slate-800 hover:border-slate-400 hover:bg-white"
  >
    <span>
      {isSelfDriveBooking(b)
        ? "Self drive"
        : b.assignedDriver?.name || "Not assigned"}
    </span>
    {!isSelfDriveBooking(b) && reassignBooking?.id === b.id && (
      <span className="ml-1 text-[10px] text-slate-400">▾</span>
    )}
  </button>

                                      {b.assignedDriver?.phoneNumber && (
                                        <div className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                                          {b.assignedDriver.phoneNumber}
                                        </div>
                                      )}

                                      {/* Reassign popover */}
                                      {reassignBooking?.id === b.id && (
                                        <div className="absolute z-20 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-3 text-[11px] shadow-xl">
                                          <div className="text-[11px] font-semibold text-slate-900">
                                            Change driver
                                          </div>

                                          {driverHasDeclined && declinedName && (
                                            <div className="mt-2 flex items-start gap-1 text-[11px] text-amber-700">
                                              <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-amber-500" />
                                              <span>
                                                <span className="font-semibold">
                                                  {declinedName}
                                                </span>{" "}
                                                has declined this booking.
                                              </span>
                                            </div>
                                          )}

                                          <div className="mt-2 relative">
                                            {(() => {
                                              const declinedSet =
                                                getDeclinedSet(reassignBooking);
                                              return (
                                                <>
                                                  <select
                                                    value={reassignDriverId}
                                                    onChange={(e) =>
                                                      setReassignDriverId(
                                                        e.target.value
                                                      )
                                                    }
                                                    className="block w-full appearance-none rounded-md border border-slate-300 bg-white px-3 py-2.5 text-[12px] text-slate-900 shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                                                  >
                                                    <option value="">
                                                      Choose driver…
                                                    </option>
                                                  {drivers.map((d) => {
    const st = getDriverSelectState(d, reassignBooking, allBookings, nowDT);
    return (
      <option key={d.id} value={String(d.id)} disabled={st.disabled}>
        {d.name}
        {d.phoneNumber ? ` (${d.phoneNumber})` : ""}
        {st.disabled ? ` — ${st.reason}` : ""}
      </option>
    );
  })}

                                                  </select>
                                                  <p className="mt-1 text-[10px] text-slate-500">
    {driverBlocksLoading
      ? "Checking driver availability…"
      : "Drivers who declined, are busy, or blocked are disabled."}
  </p>

                                                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[11px] text-slate-400">
                                                    ▾
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>

                                          <p className="mt-1 text-[10px] text-slate-500">
                                            Drivers who already declined this
                                            booking are disabled.
                                          </p>

                                          <div className="mt-3 flex justify-end gap-2">
                                            <button
                                              type="button"
                                              onClick={closeReassignPopover}
                                              className="rounded-lg border border-slate-300 px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                                            >
                                              Back
                                            </button>
                                            <button
                                              type="button"
                                              onClick={handleConfirmReassign}
                                              disabled={!reassignDriverId}
                                              className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                                            >
                                              Update driver
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-[13px] text-slate-900">
                                        {b.assignedDriver?.name || "Not assigned"}
                                      </div>
                                      {b.assignedDriver?.phoneNumber && (
                                        <div className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                                          {b.assignedDriver.phoneNumber}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            )}

                            {/* Fare */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              {renderFareCell(b)}
                            </td>

                            {/* Status */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              <StatusPill booking={b} nowDT={nowDT} />
                            </td>

                            {/* Actions */}
                            <td
                              className={`px-4 ${rowPaddingClass} align-top sm:px-5`}
                            >
                              {showPendingActions ? (
                                <div className="flex flex-col gap-2">
                                  {driverHasDeclined && declinedName && (
                                    <div className="flex items-start gap-1 text-[11px] text-amber-700">
                                      <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-amber-500" />
                                      <span>
                                        <span className="font-semibold">
                                          {declinedName}
                                        </span>{" "}
                                        has declined. Please select another
                                        driver.
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openAssignModal(b)}
                                      className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.99]"
                                    >
                                      Accept
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openCancelModal(b)}
                                      className="inline-flex items-center rounded-lg border border-rose-500 px-3 py-1.5 text-[11px] font-medium text-rose-600 hover:bg-rose-50"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : showUpcomingCancel ? (
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openCancelModal(b)}
                                    className="inline-flex items-center rounded-lg border border-rose-500 px-3 py-1.5 text-[11px] font-medium text-rose-600 hover:bg-rose-50"
                                  >
                                    Cancel booking
                                  </button>
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-400">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                
                {filteredBookings.length > 0 && (
                  <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-[11px] text-slate-600 sm:px-5">
                    <div>
                      Showing{" "}
                      <span className="font-medium">
                        {(page - 1) * pageSize + 1}
                      </span>{" "}
                      –{" "}
                      <span className="font-medium">
                        {Math.min(page * pageSize, filteredBookings.length)}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {filteredBookings.length}
                      </span>{" "}
                      bookings
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <span>Rows:</span>
                        <select
                          value={pageSize}
                          onChange={(e) => setPageSize(Number(e.target.value))}
                          className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-900"
                        >
                          {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => goToPage(page - 1)}
                          disabled={page === 1}
                          className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Prev
                        </button>
                        <span className="tabular-nums">
                          Page {page} of {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToPage(page + 1)}
                          disabled={page === totalPages}
                          className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </main>

        {/* ───── Choose vehicle for blocking dates (when filter is "All vehicles") ───── */}
        {availabilityVehicleChooserOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl">
              <h2 className="text-sm font-semibold text-slate-900">
                Choose vehicle to manage booking dates
              </h2>
              <p className="mt-1 text-[11px] text-slate-500">
                Select which vehicle&apos;s calendar you want to block. You can
                change the vehicle later from the filter on the top bar.
              </p>

              <div className="mt-4 max-h-64 space-y-2 overflow-y-auto text-[11px]">
                {vehicles.map((v) => {
                  const value =
                    v.registrationNumber || (v.id != null ? `id:${v.id}` : "");
                  const checked = availabilityVehicleChoice === value;
                  return (
                    <label
                      key={value}
                      className={[
                        "flex cursor-pointer items-center gap-3 rounded-2xl border px-3 py-2 transition",
                        checked
                          ? "border-slate-900 bg-slate-900/5"
                          : "border-slate-200 hover:border-slate-400 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <input
                        type="radio"
                        className="mt-[1px]"
                        value={value}
                        checked={checked}
                        onChange={(e) =>
                          setAvailabilityVehicleChoice(e.target.value)
                        }
                      />
                      <div className="flex flex-col">
                        <span className="text-[12px] font-semibold text-slate-900">
                          {v.brand} {v.model}
                        </span>
                        <span className="mt-0.5 inline-flex items-center rounded-[6px] bg-slate-100 px-2 py-[2px] text-[10px] font-medium text-slate-700">
                          {v.registrationNumber || "No reg"}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAvailabilityVehicleChooserOpen(false);
                    setAvailabilityVehicleChoice("");
                  }}
                  className="rounded-lg border border-slate-300 px-3.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAvailabilityVehicle}
                  className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-slate-800"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ───── Block booking dates modal ───── */}
     {/* ───── Block booking dates modal ───── */}
{availabilityModalOpen && availabilityVehicle && (
  <div className="fixed inset-0 z-40 bg-slate-900/40 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-4">
    <div className="h-full w-full overflow-y-auto bg-white shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-3xl sm:border sm:border-slate-200 sm:bg-white/95">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pr-1">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">
              Block booking dates
            </h2>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
              Click on dates to block this vehicle for full day or a time
              window. By default, all dates are open for booking.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
              <span className="text-[12px] font-semibold text-slate-900">
                {availabilityVehicle.brand} {availabilityVehicle.model}
              </span>
              <span className="inline-flex items-center rounded-[6px] bg-white px-2 py-[2px] text-[10px] font-medium text-slate-700">
                {availabilityVehicle.registrationNumber || "No reg"}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCloseAvailability}
            className="shrink-0 rounded-full border border-slate-300 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
          >
            ✕
          </button>
        </div>

        {/* Body: calendar + right panel */}
        <div className="mt-4 grid gap-4 md:mt-6 md:grid-cols-[2fr,1.4fr] md:gap-5">
          {/* Calendar */}
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3 text-[11px] text-slate-800 sm:p-4">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleMonthChange(-1)}
                  className="rounded-full border border-slate-300 px-2 py-1 text-[11px] hover:bg-white"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => handleMonthChange(1)}
                  className="rounded-full border border-slate-300 px-2 py-1 text-[11px] hover:bg-white"
                >
                  →
                </button>
              </div>

              <div className="text-[12px] font-semibold text-slate-900">
                {availabilityMonth.toLocaleString("en-IN", {
                  month: "long",
                  year: "numeric",
                })}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded border border-emerald-400 bg-white" />
                  <span>Open</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-3 w-3 rounded border border-rose-500 bg-white" />
                  <span>Blocked</span>
                </span>
              </div>
            </div>

            {availabilityLoading && (
              <div className="mb-2 text-[11px] text-slate-500">
                Loading blocked dates…
              </div>
            )}

            <div className="grid grid-cols-7 gap-1 text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div
                  key={d}
                  className="flex items-center justify-center py-1"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="mt-1 grid grid-cols-7 gap-1 text-[11px]">
              {availabilityCalendarCells.map((cell) => {
                const isBlocked = isDateBlocked(cell.iso);
                const isSelected = availabilitySelectedDate === cell.iso;
                const isPast = cell.iso < todayIso;
                const isDisabled = !cell.inMonth || isPast;

                let borderClass = isBlocked
                  ? "border-rose-500"
                  : "border-emerald-400";

                let textClass = cell.inMonth
                  ? "text-slate-900"
                  : "text-slate-300";

                if (isDisabled) {
                  textClass = "text-slate-300";
                }

                let ringClass = "";
                if (isSelected) {
                  ringClass = isBlocked
                    ? "ring-2 ring-rose-500 ring-offset-2 ring-offset-slate-50"
                    : "ring-2 ring-emerald-500 ring-offset-2 ring-offset-slate-50";
                }

                const hoverClass =
                  !isDisabled && cell.inMonth ? "hover:bg-slate-50" : "";

                const disabledClass = isDisabled
                  ? "cursor-not-allowed opacity-40"
                  : "";

                return (
                  <button
                    key={cell.iso}
                    type="button"
                    onClick={() => handleDayClick(cell)}
                    className={[
                      "flex h-10 w-full items-center justify-center rounded-lg border bg-white transition sm:h-9",
                      borderClass,
                      textClass,
                      ringClass,
                      hoverClass,
                      !cell.inMonth ? "opacity-60" : "",
                      disabledClass,
                    ].join(" ")}
                  >
                    {cell.date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right side: block form + selected date blocks */}
          <div className="rounded-2xl border border-slate-100 bg-white px-3 py-3 text-[11px] text-slate-800 sm:px-4">
            {availabilitySelectedDate ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Selected date
                    </div>
                    <div className="mt-0.5 text-[13px] font-semibold text-slate-900">
                      {fmtDateOnly(availabilitySelectedDate)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 border-t border-slate-100 pt-3">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    {/* Left label */}
                    <div className="flex w-full flex-col items-start text-[11px] text-slate-700 sm:max-w-[140px] sm:items-center">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                        Block
                      </span>
                      <span className="mt-1 text-xs font-semibold text-slate-900">
                        New slot
                      </span>
                    </div>

                    {/* Divider */}
                    <div className="hidden h-full w-px bg-slate-200 sm:block" />

                    {/* Right side */}
                    <div className="flex-1">
                      {/* Toggle */}
                      <div className="inline-flex w-full rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[10px] sm:max-w-sm">
                        <button
                          type="button"
                          onClick={() => setAvailabilityFullDay(true)}
                          className={[
                            "flex-1 rounded-md px-4 py-2 text-center font-medium whitespace-nowrap transition-colors",
                            availabilityFullDay
                              ? "bg-slate-900 text-white shadow-sm"
                              : "bg-transparent text-slate-700 hover:bg-white",
                          ].join(" ")}
                        >
                          Full day
                        </button>
                        <button
                          type="button"
                          onClick={() => setAvailabilityFullDay(false)}
                          className={[
                            "flex-1 rounded-md px-4 py-2 text-center font-medium whitespace-nowrap transition-colors",
                            !availabilityFullDay
                              ? "bg-slate-900 text-white shadow-sm"
                              : "bg-transparent text-slate-700 hover:bg-white",
                          ].join(" ")}
                        >
                          Time window
                        </button>
                      </div>

                      {/* Time window inputs */}
                      {!availabilityFullDay && (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <div className="flex flex-col">
                            <TimePicker12
                              valueHHmm={availabilityStartTime}
                              onChangeHHmm={setAvailabilityStartTime}
                              minHHmm={null}
                              disabled={!availabilitySelectedDate}
                              emptyLabel="From"
                            />
                          </div>

                          <div className="flex flex-col">
                            <TimePicker12
                              valueHHmm={availabilityEndTime}
                              onChangeHHmm={setAvailabilityEndTime}
                              minHHmm={availabilityStartTime || null}
                              disabled={!availabilitySelectedDate}
                              emptyLabel="To"
                            />
                          </div>
                        </div>
                      )}

                      {/* CTA */}
                      <button
                        type="button"
                        onClick={handleCreateAvailabilityBlock}
                        className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-rose-600 px-4 py-3 text-[11px] font-semibold text-white shadow-sm whitespace-nowrap hover:bg-rose-700 sm:max-w-sm"
                      >
                        {availabilityFullDay
                          ? "Block this date"
                          : "Block this time window"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Existing blocks for this date
                    </h3>
                    <span className="text-[11px] text-slate-500">
                      {blocksForSelectedDate.length} block
                      {blocksForSelectedDate.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {blocksForSelectedDate.length === 0 && (
                    <div className="mt-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-500">
                      This date is currently{" "}
                      <span className="font-semibold">open</span> for bookings.
                    </div>
                  )}

                  {blocksForSelectedDate.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {blocksForSelectedDate.map((blk) => {
                        const isFull = blk.fullDay;
                        const fromLabel = isFull
                          ? "Full day"
                          : fmtTimeOnly12h(blk.startDate, blk.startTime);
                        const toLabel = isFull
                          ? ""
                          : fmtTimeOnly12h(blk.endDate, blk.endTime);

                        return (
                          <div
                            key={blk.id}
                            className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-[11px] sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div>
                              <div className="text-[11px] font-semibold text-slate-900">
                                {isFull ? "Full day" : "Time window"}
                              </div>
                              {!isFull && (
                                <div className="text-[11px] text-slate-700">
                                  {fromLabel} – {toLabel}
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteAvailabilityBlock(blk.id)
                              }
                              className="inline-flex items-center justify-center rounded-lg border border-emerald-600 px-3 py-2 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 sm:ml-3"
                            >
                              Open this slot
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center text-center text-[11px] text-slate-500">
                Select a date on the calendar to manage availability.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
)}

        

        {/* ───── Assign / Accept modal ───── */}
        {assignModalBooking && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl">
              <h2 className="text-sm font-semibold text-slate-900">
                Accept booking &amp; assign driver
              </h2>

              {/* Booking summary card */}
              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-[11px] text-slate-700">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-[12px] font-semibold text-slate-900">
                    {assignModalBooking.vehicle?.brand}{" "}
                    {assignModalBooking.vehicle?.model}
                  </div>
                  <span className="inline-flex items-center rounded-[6px] bg-slate-100 px-2 py-[2px] text-[10px] font-medium text-slate-700">
                    {assignModalBooking.vehicle?.registrationNumber || "No reg"}
                  </span>
                </div>

                <div className="mt-3 space-y-1 text-[11px] leading-snug">
                  <div>
                    <span className="font-medium text-slate-600">Pickup: </span>
                    {assignModalBooking.pickupLocation || "—"}
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Drop: </span>
                    {assignModalBooking.destination || "—"}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-6 text-[11px] text-slate-600">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Pickup
                    </div>
                    <div className="mt-0.5">
                      {fmtDateOnly(assignModalBooking.pickupDate)}
                    </div>
                    <div>
                      {fmtTimeOnly12h(
                        assignModalBooking.pickupDate,
                        assignModalBooking.pickupTime
                      )}
                    </div>
                  </div>

                  <div className="hidden h-10 w-px bg-slate-200 sm:block" />

                  <div>
                    {(() => {
                      const dropDateRaw =
                        assignModalBooking.dropDate ||
                        assignModalBooking.pickupDate;
                      return (
                        <>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Drop
                          </div>
                          <div className="mt-0.5">
                            {fmtDateOnly(dropDateRaw)}
                          </div>
                          <div>
                            {fmtTimeOnly12h(
                              dropDateRaw,
                              assignModalBooking.dropTime
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

            {/* Mode selector */}
  <div className="mt-5">
    <div className="text-[11px] font-semibold text-slate-800">
      Trip type
    </div>

    <div className="mt-2 inline-flex w-full max-w-sm rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[10px]">
      <button
        type="button"
        onClick={() => {
          setAssignModalMode("with_driver");
          // keep current driver selection as-is
        }}
        className={[
          "flex-1 rounded-md px-4 py-1.5 text-center font-medium whitespace-nowrap transition-colors",
          assignModalMode === "with_driver"
            ? "bg-slate-900 text-white shadow-sm"
            : "bg-transparent text-slate-700 hover:bg-white",
        ].join(" ")}
      >
        With driver
      </button>

    <button
    type="button"
    disabled={selfDriveState.disabled}
    onClick={() => {
      if (selfDriveState.disabled) {
        toast.error(`Self drive unavailable: ${selfDriveState.reason}`);
        return;
      }
      setAssignModalMode("self_drive");
      setAssignModalDriverId("");
    }}
    className={[
      "flex-1 rounded-md px-4 py-1.5 text-center font-medium whitespace-nowrap transition-colors",
      assignModalMode === "self_drive"
        ? "bg-slate-900 text-white shadow-sm"
        : "bg-transparent text-slate-700 hover:bg-white",
      selfDriveState.disabled ? "opacity-50 cursor-not-allowed hover:bg-transparent" : "",
    ].join(" ")}
  >
    Self drive
  </button>

    </div>
  </div>

  {/* Driver selector (only when with_driver) */}
  {assignModalMode === "with_driver" ? (
    <div className="mt-5">
      <div className="flex items-baseline justify-between">
        <label className="text-[11px] font-semibold text-slate-800">
          Select driver
        </label>
        {!assignModalDriverId && (
          <span className="text-[10px] font-medium text-rose-500">
            Required
          </span>
        )}
      </div>

      <div className="mt-1 relative">
        {(() => {
          const declinedSet = getDeclinedSet(assignModalBooking);
          return (
            <>
              <select
                value={assignModalDriverId}
                onChange={(e) => setAssignModalDriverId(e.target.value)}
                className={[
                  "block w-full appearance-none border bg-white px-3 py-2.5 text-[12px] text-slate-900 shadow-sm",
                  "rounded-md border-slate-300",
                  !assignModalDriverId ? "border-amber-400" : "",
                ].join(" ")}
              >
                <option value="">Choose a driver for this trip…</option>
                {drivers.map((d) => {
    const st = getDriverSelectState(d, assignModalBooking, allBookings, nowDT);
    return (
      <option key={d.id} value={String(d.id)} disabled={st.disabled}>
        {d.name}
        {d.phoneNumber ? ` (${d.phoneNumber})` : ""}
        {st.disabled ? ` — ${st.reason}` : ""}
      </option>
    );
  })}

              </select>
              {selfDriveState.disabled && (
    <div className="mt-2 text-[10px] font-medium text-rose-600">
      Self drive unavailable: {selfDriveState.reason}
    </div>
  )}

              <p className="mt-1 text-[10px] text-slate-500">
    {driverBlocksLoading
      ? "Checking driver availability…"
      : "Busy drivers (assigned bookings) and blocked drivers (availability) are disabled."}
  </p>

              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[11px] text-slate-400">
                ▾
              </span>
            </>
          );
        })()}
      </div>

      <p className="mt-1 text-[10px] text-slate-500">
        Drivers who already declined this booking are disabled.
      </p>
    </div>
  ) : (
    <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-[11px] text-slate-700">
      This booking will be accepted as <span className="font-semibold">Self drive</span>.
      The owner will be self driving the car - Please check the wayroot driver app for ride operations.
    </div>
  )}

  <div className="mt-6 flex justify-end gap-2">
    <button
      type="button"
      onClick={() => {
        setAssignModalBooking(null);
        setAssignModalDriverId("");
        setAssignModalMode("with_driver");
      }}
      className="rounded-lg border border-slate-300 px-3.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
    >
      Back
    </button>

    <button
      type="button"
      onClick={handleConfirmAssignAndAccept}
      disabled={
    assignModalMode === "with_driver"
      ? !assignModalDriverId
      : selfDriveState.disabled
  }

      className="rounded-lg bg-emerald-600 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-400"
    >
      Accept booking
    </button>
  </div>


            
            </div>
          </div>
        )}

        {/* ───── Cancel modal ───── */}
        {cancelModalBooking && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl">
              <h2 className="text-sm font-semibold text-slate-900">
                Cancel booking
              </h2>
              {!cancelModalBooking?._isMatch ? (
                <p className="mt-1 text-[11px] leading-5 text-slate-600">
                  {`Owner cancel is allowed only until ${formatLeadTime(ownerCancelCutoffMinutes)} before pickup.`}
                </p>
              ) : null}

              {/* Booking summary card */}
              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-[11px] text-slate-700">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-[12px] font-semibold text-slate-900">
                    {cancelModalBooking.vehicle?.brand}{" "}
                    {cancelModalBooking.vehicle?.model}
                  </div>
                  <span className="inline-flex items-center rounded-[6px] bg-slate-100 px-2 py-[2px] text-[10px] font-medium text-slate-700">
                    {cancelModalBooking.vehicle?.registrationNumber || "No reg"}
                  </span>
                </div>

                <div className="mt-3 space-y-1 text-[11px] leading-snug">
                  <div>
                    <span className="font-medium text-slate-600">Pickup: </span>
                    {cancelModalBooking.pickupLocation || "—"}
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Drop: </span>
                    {cancelModalBooking.destination || "—"}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-6 text-[11px] text-slate-600">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Pickup
                    </div>
                    <div className="mt-0.5">
                      {fmtDateOnly(cancelModalBooking.pickupDate)}
                    </div>
                    <div>
                      {fmtTimeOnly12h(
                        cancelModalBooking.pickupDate,
                        cancelModalBooking.pickupTime
                      )}
                    </div>
                  </div>

                  <div className="hidden h-10 w-px bg-slate-200 sm:block" />

                  <div>
                    {(() => {
                      const dropDateRaw =
                        cancelModalBooking.dropDate ||
                        cancelModalBooking.pickupDate;
                      return (
                        <>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Drop
                          </div>
                          <div className="mt-0.5">
                            {fmtDateOnly(dropDateRaw)}
                          </div>
                          <div>
                            {fmtTimeOnly12h(
                              dropDateRaw,
                              cancelModalBooking.dropTime
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Reason textarea */}
              <div className="mt-5">
                <label className="text-[11px] font-medium text-slate-700">
                  Reason
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Internal note for your own record…"
                />
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCancelModalBooking(null);
                    setCancelReason("");
                  }}
                  className="rounded-lg border border-slate-300 px-3.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCancel}
                  className="rounded-lg border border-rose-500 px-3.5 py-1.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
                >
                  Confirm cancel
                </button>
              </div>
            </div>
          </div>
        )}

              {/* ───── Fare breakup modal ───── */}
        {fareBooking && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
            {/* Backdrop – clicking outside closes */}
            <div
              className="absolute inset-0"
              onClick={() => setFareBooking(null)}
              aria-hidden="true"
            />

            {/* Card */}
            <div className="relative z-10 w-full max-w-sm max-h-[80vh] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Fare breakup
                  </h2>
                  <p className="mt-1 text-[11px] text-slate-500">
                    {fareBooking.vehicle?.brand} {fareBooking.vehicle?.model} ·{" "}
                    {fareBooking.vehicle?.registrationNumber || "No reg"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFareBooking(null)}
                  className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
                >
                  ✕
                </button>
              </div>

              {(() => {
                const ownerFare =
                  typeof fareBooking.ownerFare === "number"
                    ? fareBooking.ownerFare
                    : fareBooking.ownerFare ?? null;
                const base =
                  typeof fareBooking.basePrice === "number"
                    ? fareBooking.basePrice
                    : fareBooking.basePrice ?? null;
                const fuel =
                  typeof fareBooking.fuelCost === "number"
                    ? fareBooking.fuelCost
                    : fareBooking.fuelCost ?? null;
                const toll =
                  typeof fareBooking.toll === "number"
                    ? fareBooking.toll
                    : fareBooking.toll ?? null;

                const formatMoney = (val) =>
                  typeof val === "number"
                    ? `₹${val.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}`
                    : "—";

                return (
                  <div className="mt-4 text-[11px] text-slate-800">
                    <dl className="space-y-1">
                      {base != null && (
                        <div className="flex justify-between">
                          <dt>Base fare</dt>
                          <dd className="tabular-nums">
                            {formatMoney(base)}
                          </dd>
                        </div>
                      )}
                      {fuel != null && (
                        <div className="flex justify-between">
                          <dt>Fuel</dt>
                          <dd className="tabular-nums">
                            {formatMoney(fuel)}
                          </dd>
                        </div>
                      )}
                      {toll != null && (
                        <div className="flex justify-between">
                          <dt>Toll / parking</dt>
                          <dd className="tabular-nums">
                            {formatMoney(toll)}
                          </dd>
                        </div>
                      )}
                    </dl>

                    <div className="mt-3 flex justify-between border-t border-slate-100 pt-2 text-[11px] font-semibold text-slate-900">
                      <span>Owner total</span>
                      <span className="tabular-nums">
                        {formatMoney(ownerFare)}
                      </span>
                    </div>

                    <p className="mt-2 text-[10px] text-slate-500">
                      Wayroot commission, API charges &amp; GST are already
                      excluded.
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}


        <Footer />
      </div>
    );
  };

  export default ManageBookings;
